// src/pages/healthcare/consultation/consultationComponents/SuggestionPanel.tsx
// COMPACT VERSION - Maximized information density

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { Button, Space, Typography, Modal, Switch, Select, Empty, Badge, Tooltip } from "antd";
import { BulbOutlined, BarChartOutlined, SettingOutlined, QuestionCircleOutlined, SearchOutlined, HeartOutlined, CompassOutlined, WarningOutlined, UserAddOutlined } from "@ant-design/icons";

// API
import { Suggestion, SuggestionStatus, SuggestionType } from "../../../../utils/healthcare/SugestionUtils";

// Components
import SuggestionPopup from "./SuggestionPopup";
import RecordCreationHandler from "./RecordCreationHandler";
import "./SuggestionPanel.css";

// Types
import { Transcript } from "../../../../types/appointments/ConsultTypes";
import { User } from "../../../../types/admin/UserTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";

const { Text } = Typography;

const iconComponents = {
	question: QuestionCircleOutlined,
	investigation: SearchOutlined,
	diagnosis: HeartOutlined,
	treatment: CompassOutlined,
	alert: WarningOutlined,
	referral: UserAddOutlined,
};

const EMPTY_COUNTS: Record<SuggestionType, number> = {
	question: 0,
	investigation: 0,
	diagnosis: 0,
	treatment: 0,
	alert: 0,
	referral: 0,
};

const EMPTY_PULSE_BADGES: Record<SuggestionType, boolean> = {
	question: false,
	investigation: false,
	diagnosis: false,
	treatment: false,
	alert: false,
	referral: false,
};

const colorMapping: { [key in SuggestionType]: string } = {
	question: "#1890ff",
	investigation: "#13c2c2",
	diagnosis: "#722ed1",
	treatment: "#9c27b0",
	alert: "#ff4d4f",
	referral: "#52c41a",
};

interface SuggestionPanelProps {
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	suggestions: Suggestion[] | null;
	updateSuggestion: (suggestionId: string, updates: Partial<Suggestion>) => void;
	aiActive: boolean;
	toggleAI: () => void;
	isDisabled?: boolean;
	transcript?: Transcript[];
	currentUser: User;
}

const SuggestionPanel: React.FC<SuggestionPanelProps> = ({ patient, setPatient, suggestions, updateSuggestion, aiActive, toggleAI, isDisabled, transcript, currentUser }) => {
	const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState<boolean>(false);
	const [showMedicationModal, setShowMedicationModal] = useState<boolean>(false);
	const [showTestOrderModal, setShowTestOrderModal] = useState<boolean>(false);
	const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);

	const prevSuggestionsRef = useRef<string[]>([]);
	const [newSuggestionIds, setNewSuggestionIds] = useState<string[]>([]);
	const [isFilterChanging, setIsFilterChanging] = useState(false);
	const [filter, setFilter] = useState<SuggestionType | "all">("all");

	const [showInsightsModal, setShowInsightsModal] = useState<boolean>(false);
	const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
	const [groupSimilarSuggestions, setGroupSimilarSuggestions] = useState<boolean>(true);
	const [maintainSuggestionPositions, setMaintainSuggestionPositions] = useState<boolean>(true);
	const [animationSpeed, setAnimationSpeed] = useState<string>("normal");

	const [counts, setCounts] = useState<Record<SuggestionType, number>>(EMPTY_COUNTS);
	const prevCountsRef = useRef<Record<SuggestionType, number>>(EMPTY_COUNTS);
	const [pulseBadges, setPulseBadges] = useState<Record<SuggestionType, boolean>>(EMPTY_PULSE_BADGES);

	const [stableSuggestionOrder, setStableSuggestionOrder] = useState<string[]>([]);

	const updateSuggestionStatus = useCallback(
		(id: string, newStatus: SuggestionStatus) => {
			if (!suggestions) return;
			updateSuggestion(id, { status: newStatus });
		},
		[suggestions, updateSuggestion]
	);

	const handleAddMedicalHistory = useCallback(
		(id: string) => {
			const suggestion = suggestions?.find((s) => s.id === id);
			if (suggestion) {
				setSelectedSuggestion(suggestion);
				setShowMedicalHistoryModal(true);
			}
		},
		[suggestions]
	);

	const handleAddMedication = useCallback(
		(id: string) => {
			const suggestion = suggestions?.find((s) => s.id === id);
			if (suggestion) {
				setSelectedSuggestion(suggestion);
				setShowMedicationModal(true);
			}
		},
		[suggestions]
	);

	const handleAddTest = useCallback(
		(id: string) => {
			const suggestion = suggestions?.find((s) => s.id === id);
			if (suggestion) {
				setSelectedSuggestion(suggestion);
				setShowTestOrderModal(true);
			}
		},
		[suggestions]
	);

	const handleRecordCreated = useCallback(
		(record: any, recordType: "medical_history" | "medication" | "test_order") => {
			if (!selectedSuggestion) return;

			updateSuggestion(selectedSuggestion.id, { suggestionRecordCreated: true });

			if (recordType === "medical_history" && record) {
				setPatient((prev) => ({
					...prev,
					medical_history: [...(prev.medical_history || []), record],
				}));
			} else if (recordType === "medication" && record) {
				setPatient((prev) => ({
					...prev,
					medications: [...(prev.medications || []), record],
				}));
			} else if (recordType === "test_order" && record) {
				setPatient((prev) => ({
					...prev,
					tests: [...(prev.tests || []), record],
				}));
			}

			setShowMedicalHistoryModal(false);
			setShowMedicationModal(false);
			setShowTestOrderModal(false);
			setSelectedSuggestion(null);
		},
		[selectedSuggestion, setPatient, updateSuggestion]
	);

	const handleFilterChange = useCallback(
		(newFilter: SuggestionType | "all") => {
			if (newFilter === filter) return;
			setIsFilterChanging(true);
			setTimeout(() => {
				setFilter(newFilter);
				setIsFilterChanging(false);
			}, 300);
		},
		[filter]
	);

	useEffect(() => {
		if (!suggestions) return;

		const visibleSuggestionIds = suggestions.filter((sug) => sug.status !== "deleted" && (filter === "all" || sug.type === filter)).map((sug) => sug.id);

		const newIds = visibleSuggestionIds.filter((id) => !stableSuggestionOrder.includes(id));
		const existingIds = stableSuggestionOrder.filter((id) => visibleSuggestionIds.includes(id));

		if (newIds.length > 0 || existingIds.length !== stableSuggestionOrder.length) {
			setStableSuggestionOrder([...existingIds, ...newIds]);
		}
	}, [suggestions, filter, stableSuggestionOrder]);

	useEffect(() => {
		if (!suggestions) return;

		const newCounts = suggestions.reduce(
			(acc, sug) => {
				if (sug.status !== "deleted" && sug.type in acc) {
					acc[sug.type]++;
				}
				return acc;
			},
			{ ...EMPTY_COUNTS }
		);

		const updatedPulseBadges = { ...EMPTY_PULSE_BADGES };
		let hasChanges = false;

		(Object.keys(newCounts) as SuggestionType[]).forEach((type) => {
			if (newCounts[type] > prevCountsRef.current[type]) {
				updatedPulseBadges[type] = true;
				hasChanges = true;
			}
		});

		if (hasChanges) {
			setPulseBadges(updatedPulseBadges);
			setTimeout(() => {
				setPulseBadges(EMPTY_PULSE_BADGES);
			}, 500);
		}

		prevCountsRef.current = { ...newCounts };
		setCounts(newCounts);
	}, [suggestions]);

	useEffect(() => {
		if (!suggestions) return;

		const currentVisibleIds = suggestions.filter((sug) => sug.status !== "deleted" && (filter === "all" || sug.type === filter)).map((sug) => sug.id);

		const previousIds = new Set(prevSuggestionsRef.current);
		const newIds = currentVisibleIds.filter((id) => !previousIds.has(id));

		if (newIds.length > 0) {
			setNewSuggestionIds(newIds);
			setTimeout(() => {
				setNewSuggestionIds([]);
			}, 5000);
		}

		prevSuggestionsRef.current = currentVisibleIds;
	}, [suggestions, filter]);

	const getBaseFilteredSuggestions = useCallback(() => {
		if (!suggestions) return [];

		const filtered = suggestions.filter((sug) => sug.status !== "deleted" && (filter === "all" || sug.type === filter));

		if (!groupSimilarSuggestions) {
			return filtered;
		}

		const seenGroups = new Set<string>();
		return filtered.filter((sug) => {
			let groupId = sug.id;
			if (seenGroups.has(groupId)) return false;
			seenGroups.add(groupId);
			return true;
		});
	}, [suggestions, filter, groupSimilarSuggestions]);

	const sortSuggestions = useCallback(
		(suggestions: Suggestion[]) => {
			return [...suggestions].sort((a, b) => {
				if (newSuggestionIds.includes(a.id) && !newSuggestionIds.includes(b.id)) {
					return -1;
				}
				if (!newSuggestionIds.includes(a.id) && newSuggestionIds.includes(b.id)) {
					return 1;
				}

				const urgencyOrder = { critical: 30, high: 20, medium: 10, low: 0 };
				const urgencyDiff = (urgencyOrder[b.urgency as keyof typeof urgencyOrder] || 0) - (urgencyOrder[a.urgency as keyof typeof urgencyOrder] || 0);

				if (urgencyDiff !== 0) return urgencyDiff;

				const typeOrder = {
					alert: 50,
					diagnosis: 40,
					treatment: 30,
					investigation: 20,
					question: 10,
					referral: 0,
				};
				const typeDiff = (typeOrder[b.type as keyof typeof typeOrder] || 0) - (typeOrder[a.type as keyof typeof typeOrder] || 0);

				if (typeDiff !== 0) return typeDiff;

				return (b.timestamp || 0) - (a.timestamp || 0);
			});
		},
		[newSuggestionIds]
	);

	const filteredSuggestions = useMemo(() => {
		const baseFiltered = getBaseFilteredSuggestions();

		if (!maintainSuggestionPositions || stableSuggestionOrder.length === 0) {
			return sortSuggestions(baseFiltered);
		}

		const suggestionMap = new Map(baseFiltered.map((s) => [s.id, s]));
		const orderedSuggestions: Suggestion[] = [];

		stableSuggestionOrder.forEach((id) => {
			const suggestion = suggestionMap.get(id);
			if (suggestion) {
				orderedSuggestions.push(suggestion);
				suggestionMap.delete(id);
			}
		});

		if (suggestionMap.size > 0) {
			const newSuggestions = Array.from(suggestionMap.values());
			const sortedNewSuggestions = sortSuggestions(newSuggestions);
			orderedSuggestions.push(...sortedNewSuggestions);
		}

		return orderedSuggestions;
	}, [getBaseFilteredSuggestions, maintainSuggestionPositions, stableSuggestionOrder, sortSuggestions]);

	const getAnimationSpeedClass = () => {
		switch (animationSpeed) {
			case "fast":
				return "suggestion-animation-fast";
			case "slow":
				return "suggestion-animation-slow";
			default:
				return "suggestion-animation-normal";
		}
	};

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			{/* Compact Header */}
			<div
				style={{
					padding: "4px 8px",
					background: "var(--background-color)",
					borderRadius: 4,
					marginBottom: 8,
					borderBottom: "1px solid var(--border-color)",
				}}
			>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
					<Space size={2}>
						<Tooltip title={aiActive ? "AI Enabled" : "AI Disabled"}>
							<Button
								type={aiActive ? "primary" : "default"}
								danger={!aiActive}
								icon={<BulbOutlined style={{ fontSize: 11 }} />}
								onClick={toggleAI}
								disabled={isDisabled}
								size="small"
								style={{ fontSize: 11, height: 24, padding: "0 6px" }}
							/>
						</Tooltip>

						<Tooltip title="Insights">
							<Button type="text" icon={<BarChartOutlined style={{ fontSize: 11 }} />} onClick={() => setShowInsightsModal(true)} size="small" style={{ fontSize: 11, height: 24, padding: "0 6px" }} />
						</Tooltip>

						<Tooltip title="Settings">
							<Button type="text" icon={<SettingOutlined style={{ fontSize: 11 }} />} onClick={() => setShowSettingsModal(true)} size="small" style={{ fontSize: 11, height: 24, padding: "0 6px" }} />
						</Tooltip>
					</Space>

					<Text type="secondary" style={{ fontSize: 10 }}>
						AI Suggestions
					</Text>
				</div>

				{/* Compact Filter Buttons */}
				<Space size={2} wrap style={{ width: "100%" }}>
					{Object.entries(counts).map(([type, count]) => {
						const Icon = iconComponents[type as SuggestionType];
						return (
							<Tooltip key={type} title={type.charAt(0).toUpperCase() + type.slice(1)}>
								<Badge count={count} size="small" className={pulseBadges[type as SuggestionType] ? "pulse-animation" : ""} style={{ fontSize: 9 }}>
									<Button
										type={filter === type ? "primary" : "text"}
										icon={
											<Icon
												style={{
													color: filter === type ? undefined : colorMapping[type as SuggestionType],
													fontSize: 10,
												}}
											/>
										}
										onClick={() => handleFilterChange(type as SuggestionType)}
										size="small"
										style={{ height: 22, width: 28, padding: 0 }}
									/>
								</Badge>
							</Tooltip>
						);
					})}
					<Button type={filter === "all" ? "primary" : "text"} onClick={() => handleFilterChange("all")} size="small" style={{ fontSize: 10, height: 22, padding: "0 6px" }}>
						All
					</Button>
				</Space>
			</div>

			{/* Suggestions List */}
			{!suggestions || suggestions.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						<Space direction="vertical" size={4}>
							<Text style={{ fontSize: 11 }}>No suggestions yet</Text>
							{!aiActive && (
								<Button type="primary" size="small" icon={<BulbOutlined />} onClick={toggleAI} style={{ fontSize: 11, height: 24 }}>
									Enable AI
								</Button>
							)}
						</Space>
					}
				/>
			) : (
				<div style={{ flex: 1, overflow: "auto", padding: "0 2px" }}>
					<TransitionGroup component="div" className={getAnimationSpeedClass()} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
						{filteredSuggestions.map((sug, index) => {
							const isNew = newSuggestionIds.includes(sug.id);

							return (
								<CSSTransition
									key={sug.id}
									timeout={{
										enter: animationSpeed === "fast" ? 400 : animationSpeed === "slow" ? 1200 : 800,
										exit: animationSpeed === "fast" ? 300 : animationSpeed === "slow" ? 900 : 600,
									}}
									classNames={{
										enter: "suggestion-enter",
										enterActive: "suggestion-enter-active",
										exit: "suggestion-exit",
										exitActive: "suggestion-exit-active",
										appear: "suggestion-appear",
										appearActive: "suggestion-appear-active",
									}}
									unmountOnExit
									appear={!isFilterChanging}
								>
									<div className={`suggestion-item ${isNew ? "suggestion-highlight-new" : ""}`}>
										<SuggestionPopup
											suggestion={sug}
											patientId={patient.id}
											onStatusChange={updateSuggestionStatus}
											onMappingUpdate={(field, newValue) => {
												setPatient((prev) => ({ ...prev, [field]: newValue }));
											}}
											onShowMedicalHistoryPreview={handleAddMedicalHistory}
											onAddMedication={handleAddMedication}
											onAddTest={handleAddTest}
											number={index}
										/>
									</div>
								</CSSTransition>
							);
						})}
					</TransitionGroup>
				</div>
			)}

			{/* Modals */}
			{selectedSuggestion && showMedicalHistoryModal && (
				<RecordCreationHandler
					isOpen={showMedicalHistoryModal}
					onClose={() => {
						setShowMedicalHistoryModal(false);
						setSelectedSuggestion(null);
					}}
					patient={patient}
					suggestion={selectedSuggestion}
					transcripts={transcript ? transcript : []}
					currentUser={currentUser}
					onRecordCreated={handleRecordCreated}
				/>
			)}

			{selectedSuggestion && showMedicationModal && (
				<RecordCreationHandler
					isOpen={showMedicationModal}
					onClose={() => {
						setShowMedicationModal(false);
						setSelectedSuggestion(null);
					}}
					patient={patient}
					suggestion={selectedSuggestion}
					transcripts={transcript ? transcript : []}
					currentUser={currentUser}
					onRecordCreated={handleRecordCreated}
				/>
			)}

			{selectedSuggestion && showTestOrderModal && (
				<RecordCreationHandler
					isOpen={showTestOrderModal}
					onClose={() => {
						setShowTestOrderModal(false);
						setSelectedSuggestion(null);
					}}
					patient={patient}
					suggestion={selectedSuggestion}
					transcripts={transcript ? transcript : []}
					currentUser={currentUser}
					onRecordCreated={handleRecordCreated}
				/>
			)}

			{/* Insights Modal */}
			<Modal title={<span style={{ fontSize: 13 }}>Clinical Insights</span>} open={showInsightsModal} onCancel={() => setShowInsightsModal(false)} width={600} footer={null}>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<div style={{ padding: 12, background: "var(--background-color)", borderRadius: 6 }}>
							<Text strong style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
								Summary
							</Text>
							<Space direction="vertical" style={{ width: "100%", fontSize: 11 }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>Total:</Text>
									<Text strong style={{ fontSize: 11 }}>
										{suggestions?.length || 0}
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>Pending:</Text>
									<Text strong style={{ fontSize: 11 }}>
										{suggestions?.filter((s) => s.status === "pending").length || 0}
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>Accepted:</Text>
									<Text strong style={{ fontSize: 11 }}>
										{suggestions?.filter((s) => s.status === "accepted").length || 0}
									</Text>
								</div>
							</Space>
						</div>

						<div style={{ padding: 12, background: "var(--background-color)", borderRadius: 6 }}>
							<Text strong style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
								Urgency
							</Text>
							<Space direction="vertical" style={{ width: "100%", fontSize: 11 }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>Critical:</Text>
									<Text strong style={{ color: "#ff4d4f", fontSize: 11 }}>
										{suggestions?.filter((s) => s.urgency === "critical").length || 0}
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>High:</Text>
									<Text strong style={{ color: "#faad14", fontSize: 11 }}>
										{suggestions?.filter((s) => s.urgency === "high").length || 0}
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text style={{ fontSize: 11 }}>Medium:</Text>
									<Text strong style={{ color: "#1890ff", fontSize: 11 }}>
										{suggestions?.filter((s) => s.urgency === "medium").length || 0}
									</Text>
								</div>
							</Space>
						</div>
					</div>
				</Space>
			</Modal>

			{/* Settings Modal */}
			<Modal title={<span style={{ fontSize: 13 }}>Settings</span>} open={showSettingsModal} onCancel={() => setShowSettingsModal(false)} width={400} footer={null}>
				<Space direction="vertical" style={{ width: "100%" }} size="small">
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: 8,
							background: "var(--background-color)",
							borderRadius: 4,
						}}
					>
						<div>
							<Text style={{ fontSize: 11, fontWeight: 500 }}>Group similar</Text>
						</div>
						<Switch size="small" checked={groupSimilarSuggestions} onChange={setGroupSimilarSuggestions} />
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: 8,
							background: "var(--background-color)",
							borderRadius: 4,
						}}
					>
						<div>
							<Text style={{ fontSize: 11, fontWeight: 500 }}>Maintain positions</Text>
						</div>
						<Switch size="small" checked={maintainSuggestionPositions} onChange={setMaintainSuggestionPositions} />
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: 8,
							background: "var(--background-color)",
							borderRadius: 4,
						}}
					>
						<div>
							<Text style={{ fontSize: 11, fontWeight: 500 }}>Animation Speed</Text>
						</div>
						<Select
							size="small"
							value={animationSpeed}
							onChange={setAnimationSpeed}
							style={{ width: 100 }}
							options={[
								{ label: "Slow", value: "slow" },
								{ label: "Normal", value: "normal" },
								{ label: "Fast", value: "fast" },
							]}
						/>
					</div>
				</Space>
			</Modal>
		</div>
	);
};

export default SuggestionPanel;
